import React from 'react';
import { Box, Stack, Typography, Rating, Chip, Divider } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { ReviewFormValues, StepKey, OTHER_SENTINEL } from '../types';
import PhotoPreviews from '../PhotoPreviews';
import { asPath } from '../utils';
import { capitalizeName } from '../../../../shared/utils';

interface SummaryProps {
    onSubmit: () => void;
    visibleKeys: StepKey[];
}

const SectionRow: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <Stack
        sx={{ pb: 1, gap: 3 }}
        direction='row'
        spacing={1}
        alignItems='flex-start'
    >
        <Typography sx={{ minWidth: 170, fontWeight: 600 }}>{label}</Typography>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Stack>
);

const Summary: React.FC<SummaryProps> = ({ visibleKeys }) => {
    const { getValues } = useFormContext<ReviewFormValues>();
    const formValues = getValues();
    const hasStep = (stepKey: StepKey) => visibleKeys.includes(stepKey);

    const renderRatingBlock = (name: string, sectionData: any) => (
        <Box>
            <SectionRow label={`${name} rating`}>
                <Stack direction='row' alignItems='center' spacing={1}>
                    {sectionData?.rating ? (
                        <>
                            <Rating
                                size='medium'
                                value={sectionData.rating}
                                readOnly
                            />
                            <Typography variant='body3'>
                                {sectionData.rating}/5
                            </Typography>
                        </>
                    ) : (
                        <Typography sx={{ ml: 1 }} variant='body3'>
                            -
                        </Typography>
                    )}
                </Stack>
            </SectionRow>
            {(sectionData?.positives?.length || 0) > 0 && (
                <SectionRow label='Positives'>
                    <Stack
                        direction='row'
                        spacing={1}
                        flexWrap='wrap'
                        useFlexGap
                    >
                        {sectionData.positives.map((positive: string) => (
                            <Chip
                                key={`p-${positive}`}
                                label={
                                    positive === OTHER_SENTINEL
                                        ? 'Something else'
                                        : capitalizeName(positive)
                                }
                                size='small'
                            />
                        ))}
                    </Stack>
                </SectionRow>
            )}
            {(sectionData?.negatives?.length || 0) > 0 && (
                <SectionRow label='Issues'>
                    <Stack
                        direction='row'
                        spacing={1}
                        flexWrap='wrap'
                        useFlexGap
                    >
                        {sectionData.negatives.map((negative: string) => (
                            <Chip
                                key={`n-${negative}`}
                                label={
                                    negative === OTHER_SENTINEL
                                        ? 'Something else'
                                        : capitalizeName(negative)
                                }
                                size='small'
                            />
                        ))}
                    </Stack>
                </SectionRow>
            )}
            {sectionData?.otherTextPositive?.trim?.() && (
                <SectionRow label='Something else (positive)'>
                    <Typography variant='body3'>
                        {sectionData.otherTextPositive}
                    </Typography>
                </SectionRow>
            )}
            {sectionData?.otherTextNegative?.trim?.() && (
                <SectionRow label='Something else (issue)'>
                    <Typography variant='body3'>
                        {sectionData.otherTextNegative}
                    </Typography>
                </SectionRow>
            )}
            <Divider sx={{ mt: 1.5 }} />
        </Box>
    );

    return (
        <Stack spacing={1} sx={{ mx: 3 }}>
            {hasStep('overall') && (
                <Box>
                    <SectionRow label='Overall rating'>
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Rating value={formValues.rating || 0} readOnly />
                            <Typography variant='body3'>
                                {formValues.rating || '-'}/5
                            </Typography>
                        </Stack>
                    </SectionRow>
                    <Divider sx={{ mt: 1.5 }} />
                </Box>
            )}
            {hasStep('cleanliness') &&
                renderRatingBlock('Cleanliness', formValues.cleanliness)}
            {hasStep('accuracy') &&
                renderRatingBlock('Accuracy', formValues.accuracy)}
            {hasStep('communication') &&
                renderRatingBlock('Communication', formValues.communication)}
            {hasStep('access') &&
                renderRatingBlock('Access', formValues.access)}
            {hasStep('value') && renderRatingBlock('Value', formValues.value)}
            <Box>
                <SectionRow label='Detailed review'>
                    <Typography variant='body3' sx={{ whiteSpace: 'pre-wrap' }}>
                        {formValues.reviewText || '—'}
                    </Typography>
                </SectionRow>
                {Array.isArray(formValues.photos) &&
                    formValues.photos.length > 0 && (
                        <SectionRow label='Photos'>
                            <PhotoPreviews
                                name={asPath('photos')}
                                disabled={true}
                            />
                        </SectionRow>
                    )}
                <Divider sx={{ mt: 1.5 }} />
            </Box>
            <Box sx={{ mb: 2 }}>
                <SectionRow label='Post anonymously'>
                    <Typography variant='body3'>
                        {formValues.isAnonymous ? 'Yes' : 'No'}
                    </Typography>
                </SectionRow>
                <Divider sx={{ mt: 1.5 }} />
            </Box>
        </Stack>
    );
};

export default Summary;
